import { auth } from '../firebase';

const API_BASE_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api`;

export interface TalkingPalData {
  system_prompt: string | null;
  switch: boolean;
  current_prompt: string | null;
  prompt_history: string[];
  nursery_rhymes: string[];
  session_limit_minutes: number;
  updatedAt: number | null;
  updatedBy: string | null;
}

/** Conversation length bounds in minutes (backend enforces the same: models.py) */
export const SESSION_LIMIT_MINUTES_MIN = 1;
export const SESSION_LIMIT_MINUTES_MAX = 10;
export const SESSION_LIMIT_MINUTES_DEFAULT = 3;

export interface TalkingPalUpdateRequest {
  system_prompt?: string;
  switch?: boolean;
  session_limit_minutes?: number;
}

export interface TalkingPalSendPromptRequest {
  prompt: string;
}

export interface NurseryRhymeRequest {
  rhyme: string;
  index?: number;
}

class TalkingPalService {
  private async getAuthHeaders(): Promise<Record<string, string>> {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }

    const token = await user.getIdToken();
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const headers = await this.getAuthHeaders();

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || 'Request failed');
    }

    return response.json();
  }

  /**
   * Get TalkingPal configuration for a device
   */
  async getTalkingPal(deviceId: string): Promise<TalkingPalData> {
    return this.request<TalkingPalData>(`/devices/${deviceId}/talking-pal/`);
  }

  /**
   * Update TalkingPal system prompt, switch and/or session length
   */
  async updateTalkingPal(
    deviceId: string,
    updates: TalkingPalUpdateRequest
  ): Promise<TalkingPalData> {
    return this.request<TalkingPalData>(`/devices/${deviceId}/talking-pal/`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  /**
   * Send an additional prompt to TalkingPal
   */
  async sendPrompt(
    deviceId: string,
    prompt: string
  ): Promise<TalkingPalData> {
    return this.request<TalkingPalData>(`/devices/${deviceId}/talking-pal/prompt`, {
      method: 'POST',
      body: JSON.stringify({ prompt }),
    });
  }

  /**
   * Clear the current prompt
   */
  async clearPrompt(deviceId: string): Promise<TalkingPalData> {
    return this.request<TalkingPalData>(`/devices/${deviceId}/talking-pal/prompt`, {
      method: 'DELETE',
    });
  }

  /**
   * Add or update a nursery rhyme
   */
  async saveNurseryRhyme(
    deviceId: string,
    rhyme: string,
    index?: number
  ): Promise<TalkingPalData> {
    const body: NurseryRhymeRequest = { rhyme };
    if (index !== undefined) {
      body.index = index;
    }
    return this.request<TalkingPalData>(`/devices/${deviceId}/talking-pal/nursery-rhymes`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  /**
   * Delete a nursery rhyme by index
   */
  async deleteNurseryRhyme(deviceId: string, index: number): Promise<TalkingPalData> {
    return this.request<TalkingPalData>(`/devices/${deviceId}/talking-pal/nursery-rhymes`, {
      method: 'DELETE',
      body: JSON.stringify({ index }),
    });
  }
}

export const talkingPalService = new TalkingPalService();
